import React from "react";
import { Box, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PeopleIcon from "@mui/icons-material/People";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import PageHeader from "../../components/PageHeader";
import Actions from "../../components/Actions";
import PackageFormDialog from "../../components/classes/PackageFormDialog";
import PackagePurchaseDialog from "../../components/classes/PackagePurchaseDialog";
import ClientPackagesDialog from "../../components/classes/ClientPackagesDialog";
import { SessionPackage } from "../../components/classes/types";
import {
  formatMoney,
  toActiveLabel,
} from "../../components/classes/classUtils";
import { useCategoryOptions } from "../../components/classes/useClassOptions";
import { getAllPackages } from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { IAction } from "../../types/table/interfaces.type";

type PackageRow = {
  id: string;
  name: string;
  price: string;
  session_count: string;
  validity_days: string;
  scope: string;
  active: string;
  raw: SessionPackage;
};

const Packages: React.FC = () => {
  const params = useParams<{ businessId?: string }>();
  const businessId = params.businessId;

  const [rows, setRows] = React.useState<PackageRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingPackage, setEditingPackage] = React.useState<SessionPackage | null>(
    null
  );
  const [purchasePackageRow, setPurchasePackageRow] =
    React.useState<SessionPackage | null>(null);
  const [clientPackagesOpen, setClientPackagesOpen] = React.useState(false);

  const { options: categoryOptions } = useCategoryOptions(true);

  React.useEffect(() => {
    let cancelled = false;

    const fetchPackages = async () => {
      setLoading(true);
      try {
        const packages = await getAllPackages();
        const mapped: PackageRow[] = packages.map((item: any) => ({
          id: String(item.id),
          name: item.name || "-",
          price: formatMoney(item.price),
          session_count: String(item.session_count ?? "-"),
          validity_days:
            item.validity_days === undefined || item.validity_days === null
              ? "-"
              : `${item.validity_days} days`,
          scope:
            item.scope_type === "CATEGORY"
              ? item.category?.name || "One category"
              : "Any class",
          active: toActiveLabel(item.active),
          raw: item,
        }));
        if (!cancelled) setRows(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to fetch packages."));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [refetchTrigger]);

  const findPackage = (id: string | number) =>
    rows.find((row) => String(row.id) === String(id))?.raw || null;

  const purchaseAction: IAction<any> = {
    id: "purchase",
    label: "Sell",
    name: "Sell",
    icon: (
      <Tooltip title="Sell to a client">
        <ShoppingCartIcon sx={{ color: "#2e7d32", width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setPurchasePackageRow(findPackage(id)),
  };

  const actions = [
    ...Actions({
      handleOpenEditModal: {
        handler: (id) => {
          setEditingPackage(findPackage(id));
          setFormOpen(true);
        },
      },
    }),
    purchaseAction,
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Packages"
          subtitle="Class packages clients can buy."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              label: "Add Package",
              onClick: () => {
                setEditingPackage(null);
                setFormOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              size: "small",
              variant: "outlined",
              label: "Client Packages",
              startIcon: <PeopleIcon />,
              onClick: () => setClientPackagesOpen(true),
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<PackageRow>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination
          loading={loading}
          fields={[
            { key: "name", header: "Package" },
            { key: "price", header: "Price" },
            { key: "session_count", header: "Sessions" },
            { key: "validity_days", header: "Validity" },
            { key: "scope", header: "Covers" },
            { key: "active", header: "Status" },
          ]}
          actions={actions}
          fetchFunc={async (tableParams?: any) => {
            const query = String((tableParams && tableParams.search) || search || "")
              .toLowerCase()
              .trim();

            const content = rows.filter((row) => {
              if (!query) return true;
              return [row.name, row.scope, row.price, row.active]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase())
                .join(" ")
                .includes(query);
            });

            const pageSize = (tableParams && tableParams.size) || 10;
            return {
              content,
              number: 0,
              size: pageSize,
              totalElements: content.length,
              totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
            };
          }}
        />

        <PackageFormDialog
          open={formOpen}
          sessionPackage={editingPackage}
          categoryOptions={categoryOptions}
          onClose={() => {
            setFormOpen(false);
            setEditingPackage(null);
          }}
          onSaved={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <PackagePurchaseDialog
          open={Boolean(purchasePackageRow)}
          businessId={businessId}
          sessionPackage={purchasePackageRow}
          onClose={() => setPurchasePackageRow(null)}
        />

        <ClientPackagesDialog
          open={clientPackagesOpen}
          businessId={businessId}
          onClose={() => setClientPackagesOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default Packages;
